import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring, AnimatePresence, useInView } from 'motion/react';
import {
  Smile, Sparkles, AlignCenter, Zap, Sun,
  ArrowRight, Star, ChevronLeft, ChevronRight, Leaf,
  Award, Users, FlaskConical, TreePine, ShieldCheck, Heart
} from 'lucide-react';
import { ForestBackground, LeafCluster } from '../components/WoodlandSVG';
import {
  fadeUp, fadeIn, fadeLeft, fadeRight, scaleIn,
  staggerContainer, staggerItem, cardHover, pageTransition, viewportConfig
} from '../components/AnimationVariants';

// ─── Animated counter ───────────────────────────────────────────────────────
function AnimatedCounter({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    const num = parseInt(target.replace(/\D/g, ''), 10);
    let start = 0;
    const duration = 1800;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * num));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, target]);

  const display = target.includes('+') ? `${count.toLocaleString()}+` :
                  target.includes('%') ? `${count}%` : target;
  return <span ref={ref}>{display}{suffix}</span>;
}

const stats = [
  { value: '15+', label: 'Years of Experience', icon: Award },
  { value: '5000+', label: 'Happy Patients', icon: Users },
  { value: '98%', label: 'Patient Satisfaction', icon: Star },
  { value: '100%', label: 'Eco-Friendly Practice', icon: Leaf },
];

const services = [
  { icon: Smile, title: 'General Dentistry', desc: 'Routine cleanings, exams, fillings, and preventive care to keep your smile healthy year-round.', to: '/services' },
  { icon: AlignCenter, title: 'Invisalign®', desc: 'Virtually invisible clear aligners to straighten your smile comfortably and discreetly at any age.', to: '/services' },
  { icon: Zap, title: 'Dental Implants', desc: 'Permanent, natural-looking tooth replacements that restore your smile and confidence for life.', to: '/services' },
  { icon: Sun, title: 'Teeth Whitening', desc: 'Professional whitening treatments that brighten your smile safely in just one visit.', to: '/services' },
  { icon: Sparkles, title: 'Porcelain Veneers', desc: 'Ultra-thin porcelain shells custom-crafted to transform the colour, shape, and size of your teeth.', to: '/services' },
  { icon: Star, title: 'Facial Aesthetics', desc: 'Anti-wrinkle injections and dermal fillers for subtle, natural-looking results that complement your smile.', to: '/services' },
];

const testimonials = [
  { name: 'Sarah M.', rating: 5, text: "I've always been nervous about the dentist, but Woodlands completely changed that. The atmosphere is so calming and the team took real time to explain everything. I felt listened to from the very first appointment.", role: 'Patient since 2021' },
  { name: 'James T.', rating: 5, text: "My whole family now comes here. The care is warm and genuine — my kids actually look forward to their visits. The staff are patient, kind, and the quality of dentistry is outstanding.", role: 'Patient since 2019' },
  { name: 'Priya K.', rating: 5, text: "I had dental implants here and the process was seamless from planning to placement. The 3D imaging gave me real confidence in the treatment plan. I feel like I have my natural teeth back.", role: 'Patient since 2020' },
  { name: 'Robert L.', rating: 5, text: "After years of avoiding the dentist, a friend recommended Woodlands. The team were so understanding — no pressure, clear explanations, and genuinely excellent care. My smile has completely transformed.", role: 'Patient since 2022' },
  { name: 'Emily W.', rating: 5, text: "The combination of specialist orthodontics and cosmetic dentistry under one roof made everything so straightforward. Professional, personal, and the results of my treatment are exactly what I hoped for.", role: 'Patient since 2023' },
];

const whyUs = [
  { icon: Heart, title: 'Gentle, Compassionate Care', desc: 'At Woodlands, dentistry is never rushed. We take time to understand what matters to you, explain your options clearly, and create a plan that feels right clinically, aesthetically, and financially.' },
  { icon: TreePine, title: 'Long-Standing Local Trust', desc: 'Built on the trusted foundations of Woodlands Orthodontics, with over 25 years serving the North Harrow community. Specialist orthodontic expertise combined with advanced general and cosmetic dentistry.' },
  { icon: FlaskConical, title: 'Advanced Diagnostic Technology', desc: 'Digital scans, intraoral cameras, CBCT imaging, and microscope-assisted endodontics support better decisions, clearer communication, and more predictable outcomes.' },
  { icon: ShieldCheck, title: 'Minimally Invasive Approach', desc: 'Our clinicians combine careful treatment planning with a minimally invasive philosophy — preserving natural tooth structure wherever possible for dentistry that feels considered, precise, and personal.' },
];

function StarRating({ count }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <motion.div key={i} initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: i * 0.08, type: 'spring', stiffness: 400 }}>
          <Star className="w-4 h-4 fill-gold text-gold" />
        </motion.div>
      ))}
    </div>
  );
}

function TestimonialsCarousel() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const go = (dir) => {
    setDirection(dir);
    setCurrent((c) => (c + dir + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const timer = setInterval(() => go(1), 5500);
    return () => clearInterval(timer);
  }, []);

  const t = testimonials[current];
  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 60 : -60 }),
    center: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
    exit: (d) => ({ opacity: 0, x: d > 0 ? -60 : 60, transition: { duration: 0.25 } }),
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="relative overflow-hidden" style={{ minHeight: 280 }} aria-live="polite" aria-atomic="true">
        <AnimatePresence custom={direction} mode="wait">
          <motion.div
            key={current}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-cream-deeper relative"
          >
            <div className="absolute top-6 right-8 text-sage-pale">
              <svg width="48" height="36" viewBox="0 0 48 36" fill="currentColor">
                <path d="M0 36V22.5C0 10.1 7.8 2.5 23.4 0l2.7 4.5C17.1 6.3 12.3 10.9 11.1 18H18V36H0zm30 0V22.5C30 10.1 37.8 2.5 53.4 0l2.7 4.5C47.1 6.3 42.3 10.9 41.1 18H48V36H30z" />
              </svg>
            </div>
            <StarRating count={t.rating} />
            <p className="mt-4 text-gray-700 text-lg leading-relaxed italic">"{t.text}"</p>
            <div className="mt-6 flex items-center gap-3">
              <motion.div
                className="w-10 h-10 rounded-full bg-sage-pale flex items-center justify-center"
                whileHover={{ scale: 1.1 }}
              >
                <span className="text-forest font-semibold text-sm">{t.name[0]}</span>
              </motion.div>
              <div>
                <div className="font-semibold text-forest">{t.name}</div>
                <div className="text-xs text-gray-500">{t.role}</div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-center gap-4 mt-6">
        <motion.button onClick={() => go(-1)} aria-label="Previous testimonial" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          className="w-10 h-10 rounded-full border-2 border-sage flex items-center justify-center hover:bg-sage hover:text-white text-sage transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest">
          <ChevronLeft className="w-5 h-5" />
        </motion.button>
        <div className="flex gap-2" role="group" aria-label="Testimonial navigation">
          {testimonials.map((_, i) => (
            <motion.button key={i} onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i); }}
              aria-label={`Go to testimonial ${i + 1}`}
              aria-current={i === current ? "true" : undefined}
              animate={{ width: i === current ? 24 : 10, backgroundColor: i === current ? '#1B3A2D' : '#D4E6C3' }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className="h-2.5 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest"
            />
          ))}
        </div>
        <motion.button onClick={() => go(1)} aria-label="Next testimonial" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          className="w-10 h-10 rounded-full border-2 border-sage flex items-center justify-center hover:bg-sage hover:text-white text-sage transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest">
          <ChevronRight className="w-5 h-5" />
        </motion.button>
      </div>
    </div>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const rawY = useTransform(scrollY, [0, 600], [0, 120]);
  const parallaxY = useSpring(rawY, { stiffness: 60, damping: 20 });
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const heroScale = useTransform(scrollY, [0, 500], [1, 1.08]);

  const heroWords = ['Where', 'Nature', 'Meets', 'Your', 'Smile'];

  return (
    <motion.div {...pageTransition} className="overflow-x-hidden">

      {/* ── Hero ── */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden bg-forest-dark pt-28">
        <motion.div className="absolute inset-0 pointer-events-none" style={{ y: parallaxY, scale: heroScale }}>
          <img
            src="/images/dentistry-reception.png"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-forest-dark/85 via-forest/75 to-forest-light/65" />
          <div className="absolute bottom-0 left-0 right-0 opacity-20">
            <ForestBackground className="w-full h-auto" />
          </div>
        </motion.div>

        {/* Floating leaves */}
        <motion.div className="absolute top-20 right-10 w-48 opacity-20 hidden md:block" aria-hidden="true"
          animate={{ y: [0, -12, 0], rotate: [0, 3, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}>
          <LeafCluster />
        </motion.div>
        <motion.div className="absolute bottom-32 left-8 w-36 opacity-15 hidden md:block" aria-hidden="true"
          animate={{ y: [0, 10, 0], rotate: [0, -4, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 2 }}>
          <LeafCluster />
        </motion.div>

        {/* Radial glow that pulses */}
        <motion.div className="absolute inset-0 pointer-events-none"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          style={{ background: 'radial-gradient(ellipse at center, rgba(197,165,90,0.1) 0%, transparent 65%)' }}
        />

        <motion.div className="relative z-10 text-center px-4 max-w-4xl mx-auto" style={{ opacity: heroOpacity }}>
          <motion.div
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-cream/90 text-sm px-4 py-2 rounded-full mb-6 border border-white/20"
            variants={fadeIn} initial="hidden" animate="visible" transition={{ delay: 0.2 }}
          >
            <motion.div animate={{ rotate: [0, 15, -10, 0] }} transition={{ duration: 3, repeat: Infinity, delay: 1 }}>
              <Leaf className="w-4 h-4 text-gold" />
            </motion.div>
            <span>Advanced Dentistry • North Harrow, London</span>
          </motion.div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-semibold text-white leading-tight text-shadow mb-6">
            <div className="flex flex-wrap justify-center gap-x-4">
              {heroWords.slice(0, 3).map((word, i) => (
                <motion.span key={word}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 + i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
                  {word}
                </motion.span>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-x-4">
              {heroWords.slice(3).map((word, i) => (
                <motion.span key={word}
                  className={word === 'Smile' ? 'text-gold' : ''}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.65 + i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
                  {word}
                </motion.span>
              ))}
            </div>
          </h1>

          <motion.p className="text-cream/80 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.85 }}>
            A calm and caring dental practice in North Harrow where advanced dentistry is delivered with warmth, clarity, and genuine attention to detail.
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row gap-4 justify-center"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 1 }}>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Link to="/contact" className="btn-primary text-base px-8 py-4">
                Book Appointment <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Link to="/services" className="btn-outline-white text-base px-8 py-4">
                Explore Treatments
              </Link>
            </motion.div>
          </motion.div>

          <motion.div className="mt-16 flex flex-col items-center gap-2 text-cream/40 text-xs"
            variants={fadeIn} initial="hidden" animate="visible" transition={{ delay: 1.3 }}>
            <span>Scroll to explore</span>
            <motion.div className="w-px h-8 bg-cream/20 relative overflow-hidden">
              <motion.div className="absolute top-0 w-px bg-gold"
                animate={{ height: ['0%', '100%', '0%'], top: ['0%', '0%', '100%'] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* ── Stats strip ── */}
      <section className="bg-gold py-10 px-4">
        <motion.div
          className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0"
          variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}
        >
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={i} variants={staggerItem}
                className={`flex flex-col items-center text-center text-white ${i < 3 ? 'md:border-r md:border-white/20' : ''}`}>
                <motion.div whileHover={{ scale: 1.2, rotate: 10 }} transition={{ type: 'spring', stiffness: 400 }}>
                  <Icon className="w-7 h-7 mb-2 opacity-80" />
                </motion.div>
                <div className="text-3xl font-serif font-bold">
                  <AnimatedCounter target={s.value} />
                </div>
                <div className="text-sm font-medium opacity-80">{s.label}</div>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* ── Welcome ── */}
      <section className="section-padding bg-cream">
        <div className="container-max">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeLeft} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <p className="section-subtitle"><Leaf className="w-4 h-4 text-sage" /> Our Philosophy</p>
              <h2 className="section-title mb-5">A Dental Practice Rooted in Comfort</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Welcome to Woodlands Dentistry, a calm and caring dental practice in North Harrow where advanced dentistry is delivered with warmth, clarity, and genuine attention to detail. Built on the trusted foundations of Woodlands Orthodontics, our practice brings together general dentistry, specialist orthodontics, restorative care, dental implants, cosmetic dentistry, facial aesthetics, and referral services in one reassuring setting.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Whether you are visiting for a routine examination, a smile transformation, a dental emergency, or specialist treatment, our aim is to help you feel listened to, informed, and completely at ease from the moment you arrive.
              </p>
              <motion.div whileHover={{ x: 4 }} transition={{ type: 'spring', stiffness: 400 }}>
                <Link to="/about" className="btn-outline text-sm">
                  Our Story <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </motion.div>

            <motion.div variants={fadeRight} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <motion.div
                className="relative rounded-3xl overflow-hidden shadow-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
              >
                <img
                  src="/images/practice-chair.png"
                  alt="Woodlands Dentistry treatment room"
                  className="w-full h-80 object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/95 via-forest-dark/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <motion.div className="text-gold text-4xl font-serif leading-none mb-2"
                    animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 3, repeat: Infinity }}>
                    "
                  </motion.div>
                  <p className="text-white font-serif italic text-lg leading-relaxed mb-4">
                    At Woodlands, dentistry is never rushed. We take time to understand what matters to you and create a plan that feels right — clinically, aesthetically, and financially.
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center border-2 border-gold flex-shrink-0">
                      <span className="text-gold font-serif font-bold text-sm">WD</span>
                    </div>
                    <div>
                      <div className="font-semibold text-white text-sm">Woodlands Dentistry</div>
                      <div className="text-cream/70 text-xs">North Harrow, London</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-sage-pale to-transparent" />
        <div className="container-max">
          <motion.div className="text-center mb-14" variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <p className="section-subtitle justify-center"><Sparkles className="w-4 h-4 text-sage" /> What We Offer</p>
            <h2 className="section-title mb-4">Our Treatments</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Woodlands Dentistry offers comprehensive care for healthy, confident smiles. From dental examinations, hygiene and gum care, fillings, crowns, bridges and root canal treatment to implants, Invisalign, whitening, veneers, composite bonding, facial aesthetics and emergency care, every treatment is planned around your needs.</p>
          </motion.div>

          <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={i} variants={staggerItem}
                  className="card-wood p-6 group cursor-default"
                  whileHover={cardHover.hover}
                  initial={cardHover.rest}
                  animate={cardHover.rest}
                >
                  <motion.div
                    className="w-12 h-12 rounded-xl bg-sage-pale flex items-center justify-center mb-4"
                    whileHover={{ scale: 1.1, backgroundColor: '#1B3A2D' }}
                    transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                  >
                    <Icon className="w-6 h-6 text-forest group-hover:text-white transition-colors" />
                  </motion.div>
                  <h3 className="font-serif font-semibold text-forest text-xl mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{s.desc}</p>
                  <motion.div whileHover={{ x: 4 }} transition={{ type: 'spring', stiffness: 400 }}>
                    <Link to={s.to} className="text-gold hover:text-gold-dark font-semibold text-sm flex items-center gap-1 transition-colors">
                      Learn More <ArrowRight className="w-4 h-4" />
                    </Link>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div className="text-center mt-10" variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Link to="/services" className="btn-primary">View All Treatments <ArrowRight className="w-5 h-5" /></Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className="section-padding bg-cream-dark relative overflow-hidden">
        <div className="container-max relative">
          <motion.div className="text-center mb-14" variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <p className="section-subtitle justify-center"><TreePine className="w-4 h-4 text-sage" /> Why Woodlands</p>
            <h2 className="section-title mb-4">The Woodlands Difference</h2>
          </motion.div>
          <motion.div className="grid sm:grid-cols-2 gap-6"
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            {whyUs.map((w, i) => {
              const Icon = w.icon;
              return (
                <motion.div key={i} variants={staggerItem}
                  className="flex gap-5 bg-white rounded-2xl p-6 shadow-sm border border-cream-deeper"
                  whileHover={{ scale: 1.02, boxShadow: '0 12px 32px rgba(45,90,61,0.1)' }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <motion.div
                    className="w-14 h-14 rounded-2xl bg-forest flex items-center justify-center flex-shrink-0"
                    whileHover={{ rotate: 8, scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                  >
                    <Icon className="w-7 h-7 text-cream" />
                  </motion.div>
                  <div>
                    <h3 className="font-serif font-semibold text-forest text-xl mb-2">{w.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{w.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <motion.div className="text-center mb-12" variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <p className="section-subtitle justify-center"><Star className="w-4 h-4 text-sage" /> Patient Stories</p>
            <h2 className="section-title mb-4">What Our Patients Say</h2>
          </motion.div>
          <motion.div variants={scaleIn} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <TestimonialsCarousel />
          </motion.div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="relative py-20 px-4 overflow-hidden bg-gradient-to-br from-forest-dark to-forest">
        <motion.div className="absolute inset-0 opacity-10 pointer-events-none"
          animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
          transition={{ duration: 20, repeat: Infinity, repeatType: 'reverse' }}>
          <ForestBackground className="absolute bottom-0 left-0 right-0 w-full" />
        </motion.div>
        <motion.div className="absolute right-0 top-0 w-64 opacity-10 hidden md:block"
          animate={{ rotate: [0, 5, 0] }} transition={{ duration: 8, repeat: Infinity }}>
          <LeafCluster />
        </motion.div>

        <motion.div className="relative z-10 max-w-3xl mx-auto text-center"
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
          <h2 className="text-3xl md:text-4xl font-serif text-white font-semibold mb-4">
            Ready to Experience Dentistry Differently?
          </h2>
          <p className="text-cream/75 text-lg mb-8 leading-relaxed">
            Whether you would like a routine check-up, a second opinion, or a conversation about improving your smile, our team is here to help. Call <a href="tel:+442088665556" className="text-gold hover:underline font-semibold">020 8866 5556</a>, request a call back, or book your appointment online.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              <Link to="/contact" className="btn-primary px-8 py-4 text-base">
                Book Your Visit Today <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              <Link to="/services" className="btn-outline-white px-8 py-4 text-base">
                Learn About Our Treatments
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </motion.div>
  );
}
